<template>
    <div>
        <div class="d-flex">
            <v-img
                :aspect-ratio="4 / 3"
                :src="$filters.imageUrl(mainImage.url, 250)"
                :lazy-src="$filters.srcHost('/placeholder.png')"
                width="150"
                max-height="112.5"
                tile
                class="me-3"
                @error="onImgError"
            >
                <template v-slot:placeholder>
                    <v-row v-show="!imgError && mainImage.url" class="fill-height ma-0" align="center" justify="center">
                        <v-progress-circular indeterminate color="grey-lighten-5" />
                    </v-row>
                </template>
            </v-img>
            <div class="ma-2 flex-grow-1 flex-shrink-1">
                <div :title="product.info.name">
                    <span class="me-2 text-primary font-weight-medium text-subtitle-1">
                        {{ product.info.name }}
                    </span>
                    <v-chip v-if="offers[0].transferType && offers[0].transferType !== 'UNKNOWN'" label size="x-small">
                        {{ $t(`filters_transferType.${offers[0].transferType}`) }}
                    </v-chip>
                    <ProductVariantLabel :product="product" class="ms-1" />
                </div>
                <supplier-name-label v-if="isTO1" :supplier-name="product.info.supplierName" />
                <transfer-operated-by-label :product="product" class="mb-2" />
                <div v-if="info.shortDescription" class="mb-4 text-body-2 text-wrap">
                    {{ info.shortDescription }}
                </div>
            </div>
        </div>
        <slot name="summary" />
        <v-divider class="mb-2" />
        <div v-for="offer in offers" :key="offer.offerKey" class="relative-position">
            <div class="d-flex align-center font-weight-bold cursor-pointer mb-1" @click="offerInfo(offer)">
                <v-icon size="small" start>mdi-information-outline</v-icon>
                <span>
                    {{ offer.info.description }}
                </span>
                <span v-if="offerTime(offer)" class="ms-2 text-grey font-weight-bold text-body-2">
                    {{ `(${offerTime(offer)})` }}
                </span>
                <ProductOfferTipsDialog v-if="offer.tipsAvailable" :offer-key="offer.offerKey" />
            </div>
            <div class="d-flex align-center">
                <div v-if="offer.info.passengerQuantity" class="mr-3 text-caption d-flex align-center">
                    <v-icon size="small" start>mdi-account</v-icon>
                    {{ offer.info.passengerQuantity }}
                </div>
                <div v-if="offer.info.baggageQuantity" class="mr-3 text-caption d-flex align-center">
                    <v-icon size="small" start>mdi-bag-checked</v-icon>
                    {{ offer.info.baggageQuantity }}
                </div>
            </div>
            <div v-if="offer.available" class="text-caption d-flex align-center">
                <v-icon :title="$t('available')" start size="small" class="text-success">
                    mdi-checkbox-marked-circle
                </v-icon>
                <span class="text-success" v-text="$t('available')" />
            </div>
            <div v-else class="text-caption d-flex align-center">
                <v-icon :title="$t('on_request')" start size="small" class="text-warning">mdi-alert-circle</v-icon>
                <span class="text-warning" v-text="$t('on_request')" />
            </div>
            <conditions-label
                v-if="showPrice"
                :offer-key="offer.offerKey"
                :price="offer.price"
                class="text-caption"
                @conditions="showConditionsInfo(offer)"
            />
            <div v-if="offer.info.tariffName" class="text-caption d-flex align-center">
                <v-icon start size="small" class="text-info">mdi-tag-text</v-icon>
                <span>
                    {{ offer.info.tariffName }}
                </span>
            </div>
            <transfer-driver-languages :lang-codes="offer.langCodes" />
            <div v-if="showPrice" class="mt-1 font-weight-bold">
                <span>
                    {{ $filters.price(offerPrice(offer)) }}
                </span>
                <commission-amount :commission="offer.price.commission" />
                <net-amount :search-request="searchRequest" :price="offer.price" />
            </div>
            <div v-else-if="free" class="mt-1 font-weight-bold">{{ $t('free') }}</div>
            <slot name="removeBtn" :offer-key="offer.offerKey" />
        </div>
        <extra-services-summary
            v-if="extraServices[0] && additionalOptions[0]"
            :extra-services="extraServices[0]"
            :additional-options="additionalOptions[0]"
        />
        <div v-if="seatsCount(productStore.seats[0])" class="mt-5">
            <p class="font-weight-bold">{{ $t('seat_selection') }}</p>
            <p class="text-caption">
                {{ $t('flights.seats') }}:
                {{ productStore.seats[0]?.map(({seatNumber}) => `${seatNumber}`).join(', ') }}
            </p>
            <strong v-if="seatsPrice(0)?.amount">
                {{ $filters.price(seatsPrice(0)) }} * {{ seatsCount(productStore.seats[0]) }} =
                {{ $filters.price(totalSeatPrice(productStore.seats[0], 0)) }}
            </strong>
        </div>
        <template v-if="!!returnTransferOffer">
            <v-divider class="my-8 d-flex" />
            <transfer-return-order-summary-card
                :info-return-transfer="infoReturnTransfer"
                :return-transfer-offer="returnTransferOffer"
                :search-request-return-transfer="searchRequestReturnTransfer"
                :extra-services="extraServices"
                :additional-options="productStore.bookingAdditionalOptions"
            >
                <template v-slot:seats>
                    <div v-if="seatsCount(productStore.seats[1])" class="mt-5">
                        <p class="font-weight-bold">{{ $t('seat_selection') }}</p>
                        <p class="text-caption">
                            {{ $t('flights.seats') }}:
                            {{ productStore.seats[1]?.map(({seatNumber}) => `${seatNumber}`).join(', ') }}
                        </p>
                        <strong v-if="seatsPrice(1)?.amount">
                            {{ $filters.price(seatsPrice(1)) }} * {{ seatsCount(productStore.seats[1]) }} =
                            {{ $filters.price(totalSeatPrice(productStore.seats[1], 1)) }}
                        </strong>
                    </div>
                </template>
            </transfer-return-order-summary-card>
        </template>
    </div>
</template>

<script lang="ts">
    import {Component, Prop} from 'vue-facing-decorator'
    import {clone} from '@/utils/helpers'
    import ConditionsLabel from '~/components/snippets/ConditionsLabel.vue'
    import {EventBus, SHOW_TRANSFER_CONDITIONS_INFO, SHOW_TRANSFER_INFO_MODAL} from '@/utils/event-bus'
    import OrderSummaryLayout from '@/components/booking/OrderSummaryLayout.vue'
    import ExtraServicesSummary from '@/components/booking/snippets/ExtraServicesSummary.vue'
    import TransferDriverLanguages from '@/components/snippets/TransferDriverLanguages.vue'
    import TransferReturnOrderSummaryCard from '@/components/transfers/booking/TransferReturnOrderSummaryCard.vue'
    import CommissionAmount from '~/components/snippets/CommissionAmount.vue'
    import NetAmount from '~src/components/snippets/NetAmount.vue'
    import SupplierNameLabel from '@/components/hotels/snippets/SupplierNameLabel.vue'
    import {OrderSummaryBase} from '@/components/booking/OrderSummaryBase.vue'
    import TransferOperatedByLabel from '@/components/transfers/snippets/TransferOperatedByLabel.vue'
    import ProductOfferTipsDialog from '~/components/modals/ProductOfferTipsDialog.vue'
    import SearchSummaryContent from '~/components/parts/SearchSummaryContent.vue'
    import ProductVariantLabel from '~/components/snippets/ProductVariantLabel.vue'

    @Component({
        components: {
            SearchSummaryContent,
            ProductVariantLabel,
            ProductOfferTipsDialog,
            TransferOperatedByLabel,
            TransferReturnOrderSummaryCard,
            TransferDriverLanguages,
            ExtraServicesSummary,
            OrderSummaryLayout,
            ConditionsLabel,
            CommissionAmount,
            NetAmount,
            SupplierNameLabel,
        },
    })
    export class TransferOrderSummary extends OrderSummaryBase {
        @Prop({required: true, type: Array}) offers
        @Prop({required: true}) info
        @Prop({required: true}) product
        @Prop({required: true}) searchRequest
        @Prop() returnTransferOffer
        @Prop() infoReturnTransfer
        @Prop() searchRequestReturnTransfer
        @Prop({required: true, type: Array}) additionalOptions

        imgError = false

        onImgError() {
            this.imgError = true
            return false
        }

        showConditionsInfo(offer) {
            EventBus.$emit(SHOW_TRANSFER_CONDITIONS_INFO, {searchRequest: this.searchRequest, offer})
        }

        offerInfo(offer) {
            EventBus.$emit(SHOW_TRANSFER_INFO_MODAL, {offer, info: this.info})
        }

        offerPrice(offer) {
            const price = this.prebooked && offer?.currentPrice ? clone(offer.currentPrice) : clone(offer.price)
            const mandatory = this.additionalOptions?.[0]?.extraServices?.filter(({mandatory}) => mandatory) || []
            if (mandatory.length) {
                mandatory.forEach(item => {
                    price.amount -= item.salesTerms.find(el => el.type === 'CLIENT').price.amount
                    price.originalAmount -= item.salesTerms.find(el => el.type === 'CLIENT').originalAmount
                })
            }
            return price
        }

        offerTime(offer) {
            if (offer.time?.length) {
                return offer.time[0]
            }
            if (offer.departureTimeFrom && offer.departureTimeTo) {
                return [offer.departureTimeFrom, offer.departureTimeTo].join(' - ')
            }

            return null
        }

        get productStore() {
            return transfersStore
        }

        get mainImage() {
            return transfersRuntimeStore.offerMainImage(this.offers[0], this.info) || null
        }

        get isTO1() {
            return authStore.isTO1
        }

        get showPrice() {
            return !this.free
        }

        seatsCount(seats) {
            return seats?.length || 0
        }

        seatsPrice(priceIndex) {
            return this.additionalOptions?.[priceIndex]?.seatSelectionServices?.[0]?.price
        }

        totalSeatPrice(seats, priceIndex) {
            let price = clone(this.seatsPrice(priceIndex))
            price.amount = price.amount * this.seatsCount(seats)
            return price
        }
    }

    export default TransferOrderSummary
</script>
